import React from 'react';

import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ListSubheader from '@mui/material/ListSubheader';
import Popper from '@mui/material/Popper';
import { alpha, styled, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { VariableSizeList } from 'react-window';

import { sharedClasses } from './sharedClasses';
import { WHITE } from '../../../ThemeContext/constants';
import { theme } from '../../../ThemeContext/ThemeObject';

import type { SxProps } from '@mui/material/styles';
import type { ListChildComponentProps } from 'react-window';

export interface FormVirtualizedAutocompleteProps {
  passOptions: (string | { value: string; label: string })[];
  value?: string | null;
  label?: string;
  error?: string;
  required?: boolean;
  loading?: boolean;
  hasIdAndRef?: boolean;
  loadingText?: string;
  setValue:
    | ((value: string) => void)
    | ((event: React.SyntheticEvent, value: string | null) => void);
  passedStyles?: SxProps;
  freeSolo?: boolean;
  dataTestId?: string;
  placeholder?: string;
  fullWidth?: boolean;
  getOptionStyle?: (label: string) => React.CSSProperties;
  disablePortal?: boolean;
  disableClearable?: boolean;
}

const acceptsEvent = (
  callback: FormVirtualizedAutocompleteProps['setValue']
): callback is (event: React.SyntheticEvent, value: string | null) => void => callback.length === 2;

const OptionStyleContext = React.createContext<
  ((label: string) => React.CSSProperties) | undefined
>(undefined);

const RenderRow = (props: ListChildComponentProps) => {
  const [displayTooltip, setDisplayTooltip] = React.useState(false);
  const getOptionStyle = React.useContext(OptionStyleContext);
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: (style.top as number) + 8
  };

  function refCallback(ref: HTMLElement) {
    if (!ref) return;
    if (ref.clientWidth > (ref.parentElement?.clientWidth ?? 0)) {
      setDisplayTooltip(true);
    }
  }

  if (Object.hasOwn(dataSet, 'group')) {
    return (
      <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
        <Tooltip
          enterDelay={600}
          placement="bottom-start"
          title={displayTooltip ? dataSet.group : ''}
        >
          <div ref={refCallback}>{dataSet.group}</div>
        </Tooltip>
      </ListSubheader>
    );
  }

  const label: string = dataSet.props.children;
  const optionStyle = getOptionStyle ? getOptionStyle(label) : {};

  return (
    <Typography
      component="li"
      {...dataSet.props}
      noWrap
      data-testid={`option-${label}`}
      style={inlineStyle}
      sx={{
        fontFamily: 'Source Sans Pro, sans-serif',
        fontSize: '16px',
        color: '#333333',
        ...optionStyle
      }}
    >
      <Tooltip enterDelay={600} placement="bottom-start" title={displayTooltip ? label : ''}>
        <div ref={refCallback}>{label}</div>
      </Tooltip>
    </Typography>
  );
};

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: number) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  (props, ref) => {
    const { children, ...other } = props;
    const itemData: React.ReactChild[] = [];
    (children as React.ReactChild[]).forEach(
      (item: React.ReactChild & { children?: React.ReactChild[] }) => {
        itemData.push(item);
        itemData.push(...(item.children ?? []));
      }
    );

    const currentTheme = useTheme();
    const smUp = useMediaQuery(currentTheme.breakpoints.up('sm'), {
      noSsr: true
    });
    const itemCount = itemData.length;
    const itemSize = smUp ? 36 : 48;

    const getChildSize = (child: React.ReactChild) => {
      if (Object.hasOwn(child, 'group')) {
        return 48;
      }

      return itemSize;
    };

    const getHeight = () => {
      if (itemCount > 8) {
        return 8 * itemSize;
      }
      return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };

    const gridRef = useResetCache(itemCount);

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <VariableSizeList
            ref={gridRef}
            height={getHeight() + 2 * 8}
            innerElementType="ul"
            itemCount={itemCount}
            itemData={itemData}
            itemSize={(index) => getChildSize(itemData[index])}
            outerElementType={OuterElementType}
            overscanCount={5}
            width="100%"
          >
            {RenderRow}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  }
);

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0
    }
  }
});

const FormVirtualizedAutocomplete = ({
  passOptions,
  value,
  setValue,
  passedStyles,
  error,
  loading,
  loadingText,
  label,
  required,
  freeSolo,
  placeholder,
  fullWidth,
  getOptionStyle,
  disablePortal = true,
  disableClearable = false,
  dataTestId = 'form-virtualized-autocomplete'
}: FormVirtualizedAutocompleteProps) => (
  <OptionStyleContext.Provider value={getOptionStyle}>
    <FormControl
      sx={{ position: 'relative', width: fullWidth ? '100%' : undefined }}
      variant="standard"
    >
      {label ? (
        <InputLabel shrink htmlFor="virtualized-autocomplete" sx={sharedClasses.inputLabel}>
          {label}
          {required ? <span>*</span> : null}
        </InputLabel>
      ) : null}
      <Autocomplete
        disableListWrap
        data-testid={dataTestId}
        disableClearable={disableClearable}
        disablePortal={disablePortal}
        forcePopupIcon={freeSolo}
        freeSolo={freeSolo}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
        id="virtualized-autocomplete"
        loading={loading}
        loadingText={loadingText}
        options={passOptions}
        isOptionEqualToValue={(option, val) => {
          if (typeof option === 'string') return option === val;
          return option.value === val || option.label === val;
        }}
        onChange={(event, newValue) => {
          if (typeof setValue === 'function') {
            const resultValue = typeof newValue === 'string' ? newValue : (newValue?.value ?? '');
            if (acceptsEvent(setValue)) {
              setValue(event, resultValue);
            } else {
              setValue(resultValue);
            }
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            error={!!error}
            placeholder={placeholder}
            variant="outlined"
            slotProps={{
              ...params.slotProps,
              inputLabel: { shrink: true, sx: { display: 'none' } }
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={props.key}>
            {typeof option === 'string' ? option : option.label}
          </li>
        )}
        slotProps={{
          popper: { component: StyledPopper },
          listbox: {
            component: ListboxComponent,
            style: sharedClasses.autoCompleteListStyles
          }
        }}
        sx={[
          { mt: label ? '24px' : 0 },
          ...(Array.isArray(passedStyles) ? passedStyles : [passedStyles ?? {}]),
          {
            ...(fullWidth ? { width: '100%' } : {}),
            '& .MuiInputBase-root': {
              borderRadius: '6px',
              backgroundColor: WHITE,
              color: '#333333',
              border: `1px solid ${error ? '#eca8a6' : '#DDDDDD'}`,
              fontSize: '16px',
              padding: '2px 10px',
              fontFamily: 'Source Sans Pro, sans-serif',
              transition: 'all 0.16s ease-in-out',
              '&:focus-within': {
                boxShadow: error
                  ? `${alpha('#E37D7A', 0.25)} 0 0 0 2px`
                  : `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 2px`,
                borderColor: error ? '#E37D7A' : theme.palette.primary.main
              }
            },
            '& fieldset': {
              display: 'none'
            },
            '& .MuiAutocomplete-input': {
              padding: '6px 0 !important'
            }
          }
        ]}
        value={
          passOptions.find((opt) =>
            typeof opt === 'string' ? opt === value : opt.value === value
          ) ?? (freeSolo ? value : null)
        }
      />
      {error ? <Box sx={sharedClasses.errorBox}>{error}</Box> : null}
    </FormControl>
  </OptionStyleContext.Provider>
);

export default FormVirtualizedAutocomplete;
